<template>
	<div
		ref="videoContainer"
		class="video-container d-flex flex-column"
		style="height: 100%; max-height: 100%"
		:class="{
			fullscreen: isFullScreen,
			playing: isPlaying,
			paused: !isPlaying,
			scrubbing: isScrubbing,
			wasPlaying: wasPlaying,
			waiting: waiting
		}"
		@click="(event) => openLightBox(recordingId, event)">
		<v-icon
			v-if="(controlsEnabled && canPlay) || showPlayButton"
			v-show="!isPlaying"
			v-test="'video-play'"
			v-track="'video-play-button'"
			:disabled="!canPlay || thumbnail"
			class="pause-indicator"
			@click="play"
			>{{ isPlaying ? "icon-pause" : "icon-play-solid" }}</v-icon
		>
		<v-progress-circular
			v-show="controlsEnabled && waiting"
			v-test="'video-loading'"
			class="loading-indicator"
			indeterminate
			size="100"
			width="7"
			color="primary">
		</v-progress-circular>
		<div v-show="controlsEnabled && videoLoading" class="video-processing" :style="{ 'border-radius': isMobile ? '12px' : '0' }">
			<div v-test="'video-loading'" class="icon">
				<v-img :src="LiveswitchVSignHandEmojiSvg" alt="Processing">
					<!-- Invisible img overlay for right-click copy functionality -->
					<img
						:src="LiveswitchVSignHandEmojiSvg"
						alt="Processing"
						style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; opacity: 0; pointer-events: auto; z-index: 1"
						@contextmenu.stop />
				</v-img>
			</div>
			<h5 class="message">Loading...</h5>
		</div>
		<video
			v-show="thumbnail || !videoLoading"
			ref="video"
			controls
			crossorigin="anonymous"
			poster="@common-ui/assets/lsc-poster.svg"
			style="height: 50%; flex: 1; min-height: 0"
			preload="metadata"
			:class="videoStyle"
			:style="{
				'border-top-left-radius': thumbnail ? 0 : isMobile ? '0px' : '12px',
				'object-fit': (limitHeight || isRecordingView) && isSafari ? 'fill' : isStarted ? 'contain' : 'cover',
				'background-color': isStarted ? '#000' : 'unset',
				'max-height': limitHeight ? '78px' : 'unset',
				height: thumbnail ? 'unset' : '50%'
			}"
			:src="source"
			:webkit-playsinline="!thumbnail"
			:playsinline="!thumbnail"
			:muted="thumbnail"
			@click="play"
			@play="onPlay"
			@pause="onPause"></video>
		<VideoTransportControls
			v-if="controlsEnabled"
			v-show="showControlBar"
			id="video-controls test-class"
			:playing="isPlaying"
			:muted="isMuted"
			:fullscreen="isFullScreen"
			:scrubbing="isScrubbing"
			:custom-controls-supported="areCustomControlSupported"
			:position="percent"
			:max-position="maxSliderValue"
			:current-time-label="currentVideoTime"
			:duration-label="videoDuration"
			@play-pause="play"
			@mute="mute"
			@fullscreen-toggle="toggleFullscreen"
			@seek="videoScrubbed"
			@seek-click="sliderClicked"
			@scrub-start="onScrubStart"
			@scrub-end="onScrubEnd"
			@track="trackTransportControl">
			<template #before-fullscreen>
				<v-btn
					id="download"
					v-test="'video-controls-download'"
					v-track="'video-controls-download-button'"
					variant="plain"
					icon
					density="comfortable"
					:disabled="isDownloading"
					@click="download">
					<v-icon v-if="!isDownloading">icon-download</v-icon>
					<v-progress-circular
						v-else
						v-test="'video-download-loading'"
						class="loading"
						color="white"
						:size="20"
						indeterminate></v-progress-circular>
				</v-btn>
				<v-btn
					v-if="!freeTrialEnded && canSnapshot && !isViewOnly"
					id="snapshot"
					v-test="'video-controls-snapshot'"
					v-track="'video-controls-snapshot-button'"
					variant="plain"
					icon
					slim
					density="comfortable"
					:disabled="currentTime == 0"
					@click="takeSnapshot">
					<v-icon>icon-snapshot</v-icon>
				</v-btn>
			</template>
			<template #after-fullscreen>
				<v-menu
					v-model="showMoreControls"
					top
					left
					:absolute="isFullScreen"
					:attach="isFullScreen ? videoContainer : false"
					minWidth="230">
					<template #activator="{ props }">
						<!-- LSP-17329: the click handler must NOT set showMoreControls itself. `v-bind="props"`
						     already binds Vuetify's activator handler, which TOGGLES the menu; a second handler
						     forcing `true` ran right after it and re-opened the menu Vuetify had just closed, so
						     the three dots could open the menu but never close it (in fullscreen there is nowhere
						     else to click, leaving pause/unpause as the only way out). The handler stays for its
						     modifiers alone: `.stop` keeps the click off the player container's own open-lightbox
						     handler. -->
						<v-btn
							v-test="'video-controls-more'"
							v-track="'video-controls-more-button'"
							variant="plain"
							icon
							density="comfortable"
							v-bind="props"
							@click.stop.prevent="() => {}">
							<v-icon>icon-dots-vertical-more</v-icon>
						</v-btn>
					</template>

					<v-list class="more-controls" rounded="xl" @click.stop.prevent="() => {}">
						<v-list-item
							v-if="canPictureInPicture"
							v-test="'video-controls-picture-in-picture'"
							v-track="'video-controls-picture-in-picture-button'"
							@click.stop.prevent="startPictureInPicture">
							<v-list-item-title><v-icon class="mr-2">icon-picture-in-picture</v-icon> Picture in picture</v-list-item-title>
						</v-list-item>
						<v-menu
							v-model="showSpeedControls"
							top
							:attach="isFullScreen ? videoContainer : undefined"
							right
							:absolute="isFullScreen"
							:max-height="300"
							:nudge-left="isFullScreen ? 64 : 0">
							<template #activator="{ props }">
								<v-list-item v-bind="props">
									<v-list-item-title v-test="'video-controls-speed'" v-track="'video-controls-speed-button'">
										<v-icon class="mr-2">icon-playback-speed</v-icon> Playback speed</v-list-item-title
									>
								</v-list-item>
							</template>

							<v-list class="speed-list" @click.stop.prevent="() => {}">
								<v-list-item @click.stop.prevent="goBackToMoreControls">
									<v-list-item-title><i class="icon-chevron-left" /> Options</v-list-item-title>
								</v-list-item>
								<v-list-item
									:class="{ active: currentPlaybackSpeed == 0.25 }"
									@click.stop.prevent="changePlaybackSpeed(0.25)">
									<v-list-item-title>0.25</v-list-item-title>
								</v-list-item>
								<v-list-item
									:class="{ active: currentPlaybackSpeed == 0.5 }"
									@click.stop.prevent="changePlaybackSpeed(0.5)">
									<v-list-item-title>0.5</v-list-item-title>
								</v-list-item>
								<v-list-item
									:class="{ active: currentPlaybackSpeed == 0.75 }"
									@click.stop.prevent="changePlaybackSpeed(0.75)">
									<v-list-item-title>0.75</v-list-item-title>
								</v-list-item>
								<v-list-item :class="{ active: currentPlaybackSpeed == 1 }" @click.stop.prevent="changePlaybackSpeed(1)">
									<v-list-item-title>Normal</v-list-item-title>
								</v-list-item>
								<v-list-item
									:class="{ active: currentPlaybackSpeed == 1.25 }"
									@click.stop.prevent="changePlaybackSpeed(1.25)">
									<v-list-item-title>1.25</v-list-item-title>
								</v-list-item>
								<v-list-item
									:class="{ active: currentPlaybackSpeed == 1.5 }"
									@click.stop.prevent="changePlaybackSpeed(1.5)">
									<v-list-item-title>1.5</v-list-item-title>
								</v-list-item>
								<v-list-item
									:class="{ active: currentPlaybackSpeed == 1.75 }"
									@click.stop.prevent="changePlaybackSpeed(1.75)">
									<v-list-item-title>1.75</v-list-item-title>
								</v-list-item>
								<v-list-item :class="{ active: currentPlaybackSpeed == 2 }" @click.stop.prevent="changePlaybackSpeed(2)">
									<v-list-item-title>2</v-list-item-title>
								</v-list-item>
								<v-list-item :class="{ active: currentPlaybackSpeed == 4 }" @click.stop.prevent="changePlaybackSpeed(4)">
									<v-list-item-title>4</v-list-item-title>
								</v-list-item>
								<v-list-item :class="{ active: currentPlaybackSpeed == 8 }" @click.stop.prevent="changePlaybackSpeed(8)">
									<v-list-item-title>8</v-list-item-title>
								</v-list-item>
							</v-list>
						</v-menu>
					</v-list>
				</v-menu>
			</template>
		</VideoTransportControls>
	</div>
</template>

<script>
	import DeviceUtility from "@/helpers/device";
	import { EventBus } from "@/helpers/eventBus";
	import { downloadFile } from "@/helpers/download";
	import { formatSeconds } from "@/helpers/time";
	import { useDisplay } from "vuetify";
	import { useToast } from "vue-toastification";
	import LiveswitchVSignHandEmojiSvg from "@common-ui/assets/liveswitch-v-sign-hand-emoji.svg";
	import VideoTransportControls from "@common-ui/media/VideoTransportControls.vue";
	import Logging from "../../plugin/track";

	// LSP-17229: tracks which VideoPlayer's videoContainer currently owns the shared vue-toastification
	// container target. Module-scoped (not per-instance data) because ownership is a fact shared across
	// every mounted VideoPlayer, not something one instance can decide alone -- a per-instance flag can't
	// tell whether another instance already reparented the container out from under it.
	let toastContainerOwner = null;

	const PlaybackState = Object.freeze({
		LOADING: "loading",
		PAUSED: "paused",
		PLAYING: "playing",
		BUFFERING: "buffering",
		SCRUBBING: "scrubbing",
		RECOVERING: "recovering",
		ENDED: "ended"
	});

	const playingStates = new Set([PlaybackState.PLAYING, PlaybackState.BUFFERING]);

	export default {
		name: "VideoPlayer",
		components: { VideoTransportControls },
		setup() {
			const display = useDisplay();
			const toast = useToast();
			return { display, toast, LiveswitchVSignHandEmojiSvg };
		},
		props: {
			contentClass: String,
			shouldPause: Boolean,
			canSnapshot: Boolean,
			fileName: String,
			recordingId: String,
			source: String,
			disablePlayback: Boolean,
			openVideoLightBox: Function,
			isSelected: Boolean,
			limitHeight: Boolean,
			isRecordingView: Boolean,
			freeTrialEnded: {
				type: Boolean,
				default: false
			},
			isViewOnly: {
				type: Boolean,
				default: false
			},
			controlsEnabled: {
				type: Boolean,
				default: true
			},
			showPlayButton: {
				type: Boolean,
				default: false
			},
			thumbnail: {
				type: Boolean,
				default: false
			}
		},
		// LSP-21125: `time-update` reports the playhead (seconds) to a parent that follows it — the lightbox
		// Translation panel highlights the caption row being spoken. Driven by the native `timeupdate` event
		// (~4 Hz) and seekTo(), never by the per-frame playback clock.
		emits: ["take-snapshot", "loaded-metadata", "playback-error", "time-update"],
		data: () => ({
			areCustomControlSupported: false,
			canPictureInPicture: true,
			isPlaybackReady: false,
			currentPlaybackSpeed: 1,
			duration: 0,
			figureId: null,
			isDownloading: false,
			isFullScreen: false,
			isMuted: false,
			isStarted: false,
			playbackState: PlaybackState.LOADING,
			playbackTransitionGeneration: 0,
			playbackOperationGeneration: 0,
			playOperationGeneration: null,
			scrubReleaseOperationGeneration: null,
			pauseOperationGeneration: null,
			resumeIntent: null,
			maxSliderValue: 1000,
			currentTime: 0,
			percent: 0,
			playActionPromise: undefined,
			playPromise: undefined,
			progress: 0,
			showMoreControls: false,
			showSpeedControls: false,
			video: {
				muted: false,
				paused: false,
				ended: false,
				currentTime: 0
			},
			videoContainer: null,
			videoReady: false,
			isMobile: false,
			isLandscape: false,
			isSafari: false,
			isPictureInPicture: false,
			animationFrameCallbackId: null,
			animationFrameGeneration: 0,
			waitingTimeoutId: null,
			waitingGeneration: 0,
			fullscreenEntryTimeoutId: null,
			videoEventHandlers: null,
			fullscreenChangeHandler: null,
			videoDeletedHandler: null
		}),
		beforeUnmount() {
			this.stopPlaybackClock();
			this.playbackOperationGeneration += 1;
			this.playActionPromise = undefined;
			this.playPromise = undefined;

			// LSP-16735: a buffering `waiting` handler schedules a deferred timeout that
			// reads this.$refs.video.readyState. If the player unmounts (lightbox closed /
			// navigated away) before it fires, $refs.video is null and the page throws.
			// Clear the timeout and detach every manually attached listener so none can
			// fire post-unmount.
			this.retireWaitingEpisode();

			// LSP-18073: toggleFullscreen()'s enter branch defers its vendor call via this timer. It
			// already re-guards on !this.$refs.videoContainer if it fires post-unmount, so this isn't a
			// crash risk, but clearing it here matches this file's own established timer-cleanup pattern
			// and avoids a routine warn firing on a normal close-within-timeout.
			if (this.fullscreenEntryTimeoutId) {
				clearTimeout(this.fullscreenEntryTimeoutId);
				this.fullscreenEntryTimeoutId = null;
			}

			const video = this.$refs.video;
			if (video && this.videoEventHandlers) {
				for (const [eventName, handler] of Object.entries(this.videoEventHandlers)) {
					video.removeEventListener(eventName, handler);
				}
			}
			this.videoEventHandlers = null;

			this.restoreToastContainerToBody();
			// This is the last call this instance will ever make -- if the verification inside
			// restoreToastContainerToBody() failed, ownership would otherwise be permanently stranded
			// pointing at a container about to be removed from the DOM (no other instance's restore is
			// gated on this one's container, so nothing would ever clear it). Force-release here
			// regardless of verification outcome.
			if (toastContainerOwner === this.videoContainer) {
				toastContainerOwner = null;
				// Also unconditionally repoint vue-toastification's own defaults.container at document.body,
				// independent of whether restoreToastContainerToBody()'s internal DOM-level verification
				// succeeded. That verification only gates whether it clears toastContainerOwner (so a
				// non-unmount caller can retry later) -- but at unmount time there is no "later": this
				// container is about to be detached from the document. If defaults.container is left
				// pointing at it, vue-toastification keeps appending every toast app-wide into a detached
				// subtree, making them all invisible for the rest of the session.
				this.toast?.updateDefaults?.({ container: document.body });
			}

			if (this.fullscreenChangeHandler) {
				document.removeEventListener("fullscreenchange", this.fullscreenChangeHandler);
				document.removeEventListener("webkitfullscreenchange", this.fullscreenChangeHandler);
				this.fullscreenChangeHandler = null;
			}

			if (this.videoDeletedHandler) {
				EventBus.$off("video-deleted", this.videoDeletedHandler);
				this.videoDeletedHandler = null;
			}
		},
		computed: {
			// LSP-19567: the control bar used to be gated on `videoReady` alone, which only flips on
			// `loadedmetadata`/`canplay`. Phone browsers routinely defer BOTH until the first user
			// gesture (data-saver, low-power mode, no-autoplay heuristics) even though `preload="metadata"`
			// asks for them, so on Android Chrome and iOS Safari the whole bar — fullscreen included —
			// stayed `display: none` until the viewer tapped the video, and there was no way to take a
			// video fullscreen from a phone. Desktop honours `preload="metadata"`, which is why the bar
			// was only ever missing on mobile.
			//
			// Scoped to `smAndDown` deliberately: on desktop the metadata arrives in milliseconds and the
			// existing gate keeps the bar from flashing an empty scrubber first. Read off `display` rather
			// than the `isMobile` data flag, which is sampled once in mounted() and does not follow a
			// rotation or resize.
			showControlBar() {
				return this.videoReady || this.display.smAndDown.value;
			},
			canPlay: {
				get() {
					return this.isPlaybackReady;
				},
				set(isReady) {
					this.isPlaybackReady = isReady;
				}
			},
			isPlaying: {
				get() {
					// Bugbot (PR #4816): BUFFERING is in playingStates so the RAF clock keeps monitoring
					// through a stall, but that alone made the icon/`.playing` class lie whenever the stall
					// started from a genuinely PAUSED video (e.g. a paused seek into unbuffered media) --
					// the button showed "pause" (and a tap could call play() via the live-DOM check in
					// play(), starting playback the viewer never asked to resume) even though nothing was
					// playing or was ever going to resume on its own. handleWaiting() always populates
					// `resumeIntent` with `owner: "buffering"` before transitioning to BUFFERING, so its
					// `shouldResume` is the one live signal that actually distinguishes "was playing, stall
					// should auto-resume" from "was paused, stall is just a paused seek."
					if (this.playbackState === PlaybackState.BUFFERING) {
						return this.resumeIntent?.owner === "buffering" ? this.resumeIntent.shouldResume : true;
					}
					return playingStates.has(this.playbackState);
				},
				set(isPlaying) {
					if (this.playbackState === PlaybackState.SCRUBBING) return;
					// Compatibility for legacy callers/tests that assigned the old data flag directly.
					// Native media events remain the only owners that start or stop the RAF clock.
					this.transitionPlaybackState(isPlaying ? PlaybackState.PLAYING : PlaybackState.PAUSED, { managePlaybackClock: false });
				}
			},
			isScrubbing() {
				return this.playbackState === PlaybackState.SCRUBBING;
			},
			waiting: {
				get() {
					return this.playbackState === PlaybackState.BUFFERING;
				},
				set(isWaiting) {
					// LSP-19523 architecture-gap round 18: this legacy-compatibility setter is the only
					// production write path to playbackState that bypasses retireWaitingEpisode(). play()'s
					// pre-metadata gesture branch clears a genuine `waiting` episode through here before
					// video.play() settles; if that promise rejects (no native "play" event, so
					// handleNativePlay's own retirement never runs), the deferred buffering timeout armed
					// earlier survives and fires 1000ms later against a state nothing will ever recover from.
					if (!isWaiting) this.retireWaitingEpisode();
					this.transitionPlaybackState(isWaiting ? PlaybackState.BUFFERING : PlaybackState.PAUSED, {
						managePlaybackClock: false
					});
				}
			},
			wasPlaying() {
				return this.resumeIntent?.owner === "scrub" && this.resumeIntent.shouldResume;
			},
			videoContainerId() {
				return `video-${this.recordingId}-container`;
			},
			videoStyle() {
				return {
					"artifact-item": this.isSelected,
					"current-recording": this.contentClass === "current-recording",
					"conversation-recording": true,
					thumbnail: this.thumbnail
				};
			},
			videoLoading() {
				const loading = !this.thumbnail && !this.videoReady && !this.waiting;
				return loading;
			},
			videoRecordingId() {
				return `video-${this.recordingId}`;
			},
			currentVideoTime() {
				return formatSeconds(this.currentTime ?? 0);
			},
			videoDuration() {
				return formatSeconds(this.duration);
			}
		},
		watch: {
			shouldPause(_newVal) {
				this.pause();
			},
			// LSP-19523 architecture-gap round 23: a parent can reuse this instance for a DIFFERENT
			// recording without unmounting it (e.g. ClipVideoDialog inside MediaLightBox, kept booted by
			// Vuetify's own lazy-content `isBooted` latch across lightbox navigation). None of the
			// playback-lifecycle bookkeeping (resumeIntent, playPromise, the generation counters, a
			// deferred buffering timeout armed by the OLD recording) is tied to `source`/`recordingId`, so
			// without this it leaks across the swap -- e.g. a stale timer armed while recording "1" was
			// stalled can fire after recording "2" has already loaded, forcing it into a spurious
			// BUFFERING state it never asked for. Forcing playbackState back to LOADING also makes
			// handleLoadedMetadata()'s existing `if (this.playbackState === PlaybackState.LOADING)` guard
			// correctly re-arm for the new recording, the same way it does on a genuine fresh mount.
			source() {
				this.resetPlaybackStateForNewSource();
			}
		},
		mounted() {
			this.isMobile = this.display.smAndDown.value;
			this.isLandscape = window.matchMedia("(orientation: landscape)").matches;
			this.isSafari = DeviceUtility.isSafari;
			const supportsVideo = !!document.createElement("video").canPlayType;

			if (supportsVideo) {
				// set up custom controls
				this.areCustomControlSupported = true;
				this.videoContainer = this.$refs.videoContainer;
				this.video = this.$refs.video;

				//hide default controls
				this.$refs.video.controls = false;
				this.$refs.video.controls = false;
				this.$refs.video.load();

				if (!("pictureInPictureEnabled" in document)) {
					this.canPictureInPicture = false;
				}

				if (DeviceUtility.isIOS && DeviceUtility.isPwaInstalled) {
					this.canPictureInPicture = false;
				}

				// LSP-16735: keep named references to every manually attached listener so
				// beforeUnmount can remove them and no deferred callback fires post-unmount.
				this.videoEventHandlers = {
					loadedmetadata: this.handleLoadedMetadata,
					progress: () => {
						// todo: can we use this to display the second loader bar?
					},
					canplay: this.handleCanPlay,
					waiting: this.handleWaiting,
					play: this.handleNativePlay,
					pause: this.handleNativePause,
					seeked: this.handleSeeked,
					// LSP-21125: the browser's own ~4 Hz playhead tick (also fired by every seek). The rAF clock that
					// drives the slider writes `currentTime` ~60x a second; forwarding THAT re-rendered a listening
					// parent (the whole lightbox) at frame rate.
					timeupdate: () => {
						const video = this.$refs.video;
						if (video) this.$emit("time-update", video.currentTime);
					},
					ended: this.handleEnded,
					enterpictureinpicture: () => {
						this.isPictureInPicture = true;
					},
					leavepictureinpicture: () => {
						this.isPictureInPicture = false;
					},
					error: () => {
						this.$emit("playback-error", {
							recordingId: this.recordingId,
							source: this.source
						});
					},
					webkitendfullscreen: () => {
						// Use the same identity check as fullscreenChangeHandler (LSP-18073) and the same
						// vendor-prefixed union as syncToastContainerForFullscreen() (called right below) --
						// the previous ad hoc check here (document.fullScreenElement with a capital S isn't a
						// real DOM property in any browser, and it omitted fullscreenElement/
						// webkitFullscreenElement) could disagree with those calls' fullscreen determination,
						// letting isFullScreen and the toast-reparent state desync (LSP-17229 PR #3570 review
						// round 4). The identity check (not bare truthiness) also keeps data-fullscreen/the
						// .fullscreen class in sync on this path, matching fullscreenChangeHandler.
						this.syncFullscreenStateFromDom();
						this.syncToastContainerForFullscreen();
					}
				};

				for (const [eventName, handler] of Object.entries(this.videoEventHandlers)) {
					this.$refs.video.addEventListener(eventName, handler);
				}

				this.fullscreenChangeHandler = () => {
					// LSP-18073: delegates to syncFullscreenStateFromDom(), which uses the vendor-prefixed
					// union (via currentFullscreenElement()) rather than the bare standard property -- this
					// handler now also runs on webkitfullscreenchange (added for LSP-17229), and on
					// WebKit-only fullscreen only document.webkitFullscreenElement is set, not
					// document.fullscreenElement. Unlike the old bare `if (!currentFullscreenElement())
					// isFullScreen = false` body, this can also flip isFullScreen to true and syncs
					// data-fullscreen/the .fullscreen class, not just clear them.
					this.syncFullscreenStateFromDom();
					this.syncToastContainerForFullscreen();
				};
				document.addEventListener("fullscreenchange", this.fullscreenChangeHandler);
				document.addEventListener("webkitfullscreenchange", this.fullscreenChangeHandler);

				this.videoDeletedHandler = (ids) => {
					if (ids.includes(this.recordingId) && this.isPictureInPicture && document.pictureInPictureElement) {
						this.isPictureInPicture = false;
						document.exitPictureInPicture();
					}
				};
				EventBus.$on("video-deleted", this.videoDeletedHandler);
			}
		},
		methods: {
			trackTransportControl(name, testId) {
				Logging.log({ name: `${name}-clicked`, data: { action: "click", testId, node: "button" } });
			},
			transitionPlaybackState(nextState, { managePlaybackClock = true } = {}) {
				// LSP-19523 architecture-gap round 19: rounds 17/18 each fixed one specific write path
				// (onScrubStart/onScrubEnd, then the legacy `waiting` setter) that could leave a deferred
				// buffering timeout armed past the point it was still valid. Round 19 found a third: a
				// native "pause" event NOT caused by this component's own pause() (PiP controls, the OS
				// media session, or any other script calling video.pause() directly) reaches
				// handleNativePause() and transitions to PAUSED without retiring anything either. Patching
				// call sites one at a time as they're found is the wrong shape of fix -- centralize it here
				// instead: ANY transition to a non-BUFFERING state invalidates a pending buffering episode,
				// regardless of which write path drives it, so no future caller can reintroduce this class
				// of bug by forgetting to call retireWaitingEpisode() itself.
				//
				// Transitions INTO BUFFERING must NOT retire here: handleWaiting's own timeout callback
				// sets resumeIntent.generation to the current waitingGeneration immediately before calling
				// this with BUFFERING, and retiring here would bump waitingGeneration again and desync
				// handleCanPlay's `bufferingIntent.generation === this.waitingGeneration - 1` resume check.
				if (nextState !== PlaybackState.BUFFERING) this.retireWaitingEpisode();

				if (this.playbackState === nextState) return this.playbackTransitionGeneration;

				const wasPlayingState = playingStates.has(this.playbackState);
				const willBePlayingState = playingStates.has(nextState);
				this.playbackState = nextState;
				this.playbackTransitionGeneration += 1;

				if (managePlaybackClock && wasPlayingState && !willBePlayingState) {
					this.stopPlaybackClock();
				} else if (managePlaybackClock && !wasPlayingState && willBePlayingState) {
					this.startPlaybackClock();
				}

				return this.playbackTransitionGeneration;
			},
			startPlaybackClock() {
				if (this.animationFrameCallbackId !== null) return;

				this.animationFrameGeneration += 1;
				this.schedulePlaybackFrame(this.animationFrameGeneration);
			},
			schedulePlaybackFrame(generation) {
				if (generation !== this.animationFrameGeneration || !playingStates.has(this.playbackState)) return;

				this.animationFrameCallbackId = requestAnimationFrame(() => {
					if (generation !== this.animationFrameGeneration) return;
					this.animationFrameCallbackId = null;
					this.updateSlider(generation);
				});
			},
			stopPlaybackClock() {
				this.animationFrameGeneration += 1;
				if (this.animationFrameCallbackId !== null) {
					cancelAnimationFrame(this.animationFrameCallbackId);
					this.animationFrameCallbackId = null;
				}
			},
			retireWaitingEpisode() {
				this.waitingGeneration += 1;
				if (this.waitingTimeoutId !== null) {
					clearTimeout(this.waitingTimeoutId);
					this.waitingTimeoutId = null;
				}
			},
			// LSP-19523 architecture-gap round 23: reinitialize every piece of playback-lifecycle
			// bookkeeping that is scoped to "which recording is currently loaded", not to "this component
			// instance." Invalidating every generation counter (not just bumping playbackOperationGeneration)
			// guarantees any in-flight continuation from the OLD recording's play()/pause() is a no-op the
			// next time it checks its captured generation against the live one.
			resetPlaybackStateForNewSource() {
				this.stopPlaybackClock();
				this.retireWaitingEpisode();
				this.resumeIntent = null;
				this.playActionPromise = undefined;
				this.playPromise = undefined;
				this.playbackOperationGeneration += 1;
				this.playOperationGeneration = null;
				this.pauseOperationGeneration = null;
				this.scrubReleaseOperationGeneration = null;
				this.isPlaybackReady = false;
				this.videoReady = false;
				this.isStarted = false;
				this.duration = 0;
				this.currentTime = 0;
				this.percent = 0;
				this.transitionPlaybackState(PlaybackState.LOADING);
			},
			handleLoadedMetadata() {
				this.isPlaybackReady = true;
				this.videoReady = true;
				if (this.playbackState === PlaybackState.LOADING) {
					this.transitionPlaybackState(PlaybackState.PAUSED);
				}
				this.$emit("loaded-metadata", this.video, this.recordingId);
				this.duration = this.$refs.video?.duration ?? 0;

				if (this.duration > 180) {
					this.maxSliderValue = 1000;
				}

				this.syncPlaybackPosition();
			},
			handleCanPlay() {
				const video = this.$refs.video;
				if (!video) return;

				const bufferingIntent = this.resumeIntent;
				this.retireWaitingEpisode();
				this.$emit("loaded-metadata", this.video, this.recordingId);
				this.isPlaybackReady = true;
				this.videoReady = true;

				if (this.playbackState !== PlaybackState.BUFFERING) return;

				const shouldResume =
					bufferingIntent?.owner === "buffering" &&
					bufferingIntent.generation === this.waitingGeneration - 1 &&
					bufferingIntent.shouldResume;
				this.resumeIntent = null;
				this.transitionPlaybackState(shouldResume ? PlaybackState.PLAYING : PlaybackState.PAUSED);
				if (shouldResume) {
					this.startPlaybackRequest("Playback recovery play() was rejected after buffering");
				}
			},
			handleWaiting() {
				if (this.playbackState === PlaybackState.ENDED) return;

				const committedBufferingIntent =
					this.playbackState === PlaybackState.BUFFERING && this.resumeIntent?.owner === "buffering" ? this.resumeIntent : null;
				this.retireWaitingEpisode();
				const waitingGeneration = this.waitingGeneration;
				const video = this.$refs.video;
				const shouldResume = committedBufferingIntent
					? committedBufferingIntent.shouldResume
					: playingStates.has(this.playbackState) || this.playbackState === PlaybackState.RECOVERING || !video?.paused;
				if (committedBufferingIntent) {
					this.resumeIntent = {
						...committedBufferingIntent,
						generation: waitingGeneration,
						shouldResume
					};
				}
				this.waitingTimeoutId = window.setTimeout(() => {
					if (waitingGeneration !== this.waitingGeneration) return;
					this.waitingTimeoutId = null;
					const currentVideo = this.$refs.video;
					if (!currentVideo || this.playbackState === PlaybackState.ENDED || this.playbackState === PlaybackState.SCRUBBING)
						return;
					// https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/readyState
					if (currentVideo.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) return;

					this.isPlaybackReady = false;
					this.resumeIntent = {
						owner: "buffering",
						generation: waitingGeneration,
						shouldResume
					};
					this.transitionPlaybackState(PlaybackState.BUFFERING);
				}, 1000);
			},
			handleNativePlay() {
				const video = this.$refs.video;
				const isSupersededPlay =
					this.playOperationGeneration !== null &&
					this.playOperationGeneration < this.playbackOperationGeneration &&
					video?.paused;
				if (isSupersededPlay) return;

				this.retireWaitingEpisode();
				this.isPlaybackReady = true;
				if (this.playbackState === PlaybackState.SCRUBBING) return;

				this.resumeIntent = null;
				this.playPromise = undefined;
				this.transitionPlaybackState(PlaybackState.PLAYING);
			},
			handleNativePause() {
				const video = this.$refs.video;
				const isSupersededPause =
					this.pauseOperationGeneration !== null &&
					this.pauseOperationGeneration < this.playbackOperationGeneration &&
					!video?.paused;
				if (isSupersededPause) return;

				if (this.playbackState === PlaybackState.ENDED) return;
				if (this.playbackState === PlaybackState.SCRUBBING) {
					this.restoreScrubOwnedPosition();
					return;
				}
				this.syncPlaybackPosition();
				this.transitionPlaybackState(PlaybackState.PAUSED);
			},
			handleSeeked() {
				if (this.playbackState === PlaybackState.ENDED) return;

				const video = this.$refs.video;
				if (!video) return;
				if (this.playbackState === PlaybackState.SCRUBBING) {
					this.restoreScrubOwnedPosition(video);
					return;
				}

				this.syncPlaybackPosition(video);
			},
			handleEnded() {
				const video = this.$refs.video;
				const hasNewerScrubRelease =
					this.scrubReleaseOperationGeneration === this.playbackOperationGeneration &&
					(this.playbackState === PlaybackState.PAUSED ||
						this.playbackState === PlaybackState.RECOVERING ||
						this.playbackState === PlaybackState.PLAYING) &&
					!video?.ended;
				if (hasNewerScrubRelease) return;

				const hasNewerNonTerminalScrubPosition =
					this.playbackState === PlaybackState.SCRUBBING &&
					this.resumeIntent?.owner === "scrub" &&
					this.resumeIntent.hasPositionUpdate &&
					!this.resumeIntent.endedDuringScrub;
				this.retireWaitingEpisode();
				this.playbackOperationGeneration += 1;
				this.playPromise = undefined;
				this.isPlaybackReady = true;

				if (this.playbackState === PlaybackState.SCRUBBING) {
					if (this.resumeIntent?.owner === "scrub" && !hasNewerNonTerminalScrubPosition) {
						this.resumeIntent.endedDuringScrub = true;
					}
					this.stopPlaybackClock();
				} else {
					this.resumeIntent = null;
					this.transitionPlaybackState(PlaybackState.ENDED);
				}

				if (!video) return;
				if (this.isPictureInPicture && this.playbackState !== PlaybackState.SCRUBBING) {
					video.currentTime = 0;
				}
				this.syncPlaybackPosition(video);
				if (!this.isPictureInPicture && !hasNewerNonTerminalScrubPosition) this.percent = this.maxSliderValue;
			},
			startPlaybackRequest(rejectionMessage) {
				const video = this.$refs.video;
				if (!video || typeof video.play !== "function") return undefined;

				const operationGeneration = ++this.playbackOperationGeneration;
				this.playOperationGeneration = operationGeneration;
				const playPromise = video.play();
				this.playPromise = playPromise;
				if (playPromise?.catch) {
					playPromise.catch((error) => {
						if (operationGeneration !== this.playbackOperationGeneration) return;
						this.playPromise = undefined;
						this.retireWaitingEpisode();
						this.resumeIntent = null;
						if (
							this.playbackState === PlaybackState.RECOVERING ||
							this.playbackState === PlaybackState.PLAYING ||
							this.playbackState === PlaybackState.BUFFERING
						) {
							this.transitionPlaybackState(PlaybackState.PAUSED);
						}
						console.warn(rejectionMessage, {
							recordingId: this.recordingId,
							error
						});
					});
				}
				return playPromise;
			},
			syncPlaybackPosition(video = this.$refs.video) {
				if (!video) return;

				const hasUsableDuration = Number.isFinite(this.duration) && this.duration > 0;
				this.percent =
					!hasUsableDuration || video.currentTime < 0.2
						? 0
						: Math.floor((video.currentTime / this.duration) * this.maxSliderValue);
				this.currentTime = video.currentTime;
			},
			/**
			 * LSP-21125: move the playhead for a parent (the lightbox Translation panel's click-to-seek). Keeps the
			 * play state: a playing video continues from the new position, a paused one stays paused there. From
			 * ENDED it settles to PAUSED, because handleSeeked ignores seeks while ENDED and the next play would
			 * otherwise not start from the chosen row. Ignored mid-scrub — the viewer's drag owns the playhead.
			 */
			seekTo(seconds) {
				const video = this.$refs.video;
				if (!video || !Number.isFinite(seconds) || this.playbackState === PlaybackState.SCRUBBING) return;
				const hasUsableDuration = Number.isFinite(this.duration) && this.duration > 0;
				const targetSeconds = Math.max(0, hasUsableDuration ? Math.min(seconds, this.duration) : seconds);
				if (this.playbackState === PlaybackState.ENDED) {
					this.transitionPlaybackState(PlaybackState.PAUSED);
				}
				video.currentTime = targetSeconds;
				this.syncPlaybackPosition(video);
				// Report at once: an unloaded or undecodable source fires no timeupdate for this seek.
				this.$emit("time-update", video.currentTime);
			},
			restoreScrubOwnedPosition(video = this.$refs.video) {
				if (video && video.currentTime !== this.currentTime) video.currentTime = this.currentTime;
			},
			currentFullscreenElement() {
				return (
					document.fullscreenElement ||
					document.webkitFullscreenElement ||
					document.webkitCurrentFullScreenElement ||
					document.mozFullScreenElement ||
					document.msFullscreenElement
				);
			},
			restoreToastContainerToBody() {
				const container = this.$refs.videoContainer;
				if (!container || !this.toast) {
					// Gate on THIS instance's own stable container reference, not global ownership
					// truthiness -- toastContainerOwner is module-scoped and shared across every mounted
					// VideoPlayer, so a bare truthiness check would misattribute another instance's
					// ownership to this one's recordingId.
					if (toastContainerOwner && toastContainerOwner === this.videoContainer) {
						console.warn(
							"LSP-17229: cannot restore snapshot toast to document.body -- videoContainer or toast is unexpectedly unavailable",
							{
								recordingId: this.recordingId
							}
						);
					}
					return;
				}
				if (toastContainerOwner === container) {
					this.toast.updateDefaults({ container: document.body });

					// Verify synchronously (see the comment in syncToastContainerForFullscreen() for why
					// this is reliable). A failed restore here is NOT a benign fallback: `.video-container`
					// sets a CSS transform (containing block for position:fixed descendants) and
					// overflow:hidden, so a toast root stranded inside a non-fullscreen `.video-container`
					// would be clipped to that small box -- invisible, i.e. LSP-17229 recurring. Only clear
					// ownership once verified absent from `container` -- if verification fails, ownership
					// stays so a later call (another fullscreenchange, or beforeUnmount) retries instead of
					// abandoning a still-broken restore.
					if (container.querySelector(".Vue-Toastification__container")) {
						console.warn("LSP-17229: snapshot toast restore to document.body appears to have silently failed", {
							recordingId: this.recordingId
						});
					} else {
						toastContainerOwner = null;
					}
				}
			},
			syncToastContainerForFullscreen() {
				const container = this.$refs.videoContainer;
				if (!container || !this.toast) {
					// Gate on this instance's own ownership, same as restoreToastContainerToBody() --
					// this method runs on every mounted instance for every fullscreenchange event, so an
					// unrelated instance's transiently-falsy container/toast would otherwise misattribute
					// a warning under its own recordingId even though it never owned anything.
					if (toastContainerOwner && toastContainerOwner === this.videoContainer) {
						console.warn(
							"LSP-17229: cannot sync snapshot toast for fullscreen -- videoContainer or toast is unexpectedly unavailable",
							{
								recordingId: this.recordingId
							}
						);
					}
					return;
				}

				if (this.currentFullscreenElement() === container) {
					// toastContainerOwner === container alone isn't enough to skip the call: it only
					// means we've previously *attempted* a claim, not that the DOM move actually
					// succeeded. Re-check the DOM directly and retry the claim whenever it hasn't landed,
					// regardless of ownership -- otherwise a silently failed reparent would be diagnosed
					// once and then never retried on subsequent identical events.
					if (!container.querySelector(".Vue-Toastification__container")) {
						this.toast.updateDefaults({ container });
						toastContainerOwner = container;
						// Verify synchronously, not just on a later repeat call: setup()'s only await is
						// gated behind `isFunction(container)`, which is always false for the DOM element
						// we pass, so its whole body -- including the real appendChild -- runs
						// synchronously before updateDefaults() returns.
						if (!container.querySelector(".Vue-Toastification__container")) {
							console.warn("LSP-17229: snapshot toast reparent into fullscreen container appears to have silently failed", {
								recordingId: this.recordingId
							});
						}
					} else {
						toastContainerOwner = container;
					}
				} else {
					this.restoreToastContainerToBody();
				}
			},
			updateSlider(animationFrameGeneration = null) {
				if (animationFrameGeneration !== null && animationFrameGeneration !== this.animationFrameGeneration) return;
				if (this.isScrubbing) return;

				const video = this.$refs.video;
				if (!video) return;

				this.syncPlaybackPosition(video);

				if (playingStates.has(this.playbackState) && this.animationFrameCallbackId === null) {
					this.schedulePlaybackFrame(this.animationFrameGeneration);
				}
			},
			changePlaybackSpeed(speed) {
				const videoEl = this.$refs.video;
				videoEl.playbackRate = speed;
				videoEl.defaultPlaybackRate = speed;
				// Read the rate back: WebKit/Safari clamps playbackRate (~2x), so reflect what the
				// browser actually applied rather than the requested speed to keep the active menu item honest.
				this.currentPlaybackSpeed = videoEl.playbackRate;
				window.setTimeout(() => {
					this.showSpeedControls = false;
					this.showMoreControls = false;
				}, 0);
			},
			controlsClicked(e) {
				e.stopPropagation();
				e.preventDefault();
			},
			openLightBox(recordingId, event) {
				// LSP-17229: while fullscreen, the snapshot toast is reparented inside this container
				// (see syncToastContainerForFullscreen) so a click on the toast bubbles up here too --
				// ignore it, it's not a click on the video thumbnail.
				if (event?.target?.closest?.(".Vue-Toastification__container")) return;
				if (this.openVideoLightBox) {
					this.openVideoLightBox(recordingId);
				}
			},
			takeSnapshot() {
				const video = this.$refs.video;
				if (video) {
					this.$emit("take-snapshot", video, this.recordingId);
				}
			},
			async download(e) {
				e.preventDefault();
				this.isDownloading = true;
				try {
					await downloadFile(this.source, this.fileName);
				} catch {
					//caught in downloadFile function
				} finally {
					this.isDownloading = false;
				}
			},
			goBackToMoreControls() {
				window.setTimeout(() => {
					this.showSpeedControls = false;
					this.showMoreControls = true;
				}, 0);
			},
			mute() {
				this.isMuted = !this.isMuted;
				this.$refs.video.muted = !this.$refs.video.muted;
			},
			async play() {
				if (this.thumbnail) {
					return;
				}
				if (this.isScrubbing) return;

				const video = this.$refs.video;
				if (!video) {
					console.warn("Tried playing video before video element is loaded.");
					return;
				}

				if (!this.canPlay && !this.isPlaying) {
					// LSP-19567: this branch used to swallow the gesture and return. That was invisible while
					// the control bar was hidden until metadata arrived, but the bar is now visible on phones
					// BEFORE metadata (see `showControlBar`), so a real tap reaches here for the first time —
					// and swallowing it makes the bar look dead: nothing plays, and toggleFullscreen()'s iOS
					// pre-roll (`await this.play()`) hands `webkitEnterFullScreen()` an element with no loaded
					// metadata, which WebKit rejects. The tap is a user gesture, which is precisely what the
					// phone browser was waiting for before loading anything, so play it rather than refuse it.
					// Rejections are contained here: the template binds `@click="play"` without awaiting, so a
					// propagated rejection would be unhandled.
					// Scoped to `!isPlaying` deliberately (review): the `waiting` handler flips `canPlay` back
					// to false during a mid-playback buffering stall while `isPlaying` stays true, so without
					// that guard a tap on the visibly-showing PAUSE button during a stall would be routed to
					// play() instead of pause().
					//
					// This is also the diagnostic the silent-failure rule asks for, and the only moment worth
					// logging: neither half of the bar's gate is a failure on its own (a thumbnail player
					// legitimately has `controlsEnabled: false`, and pre-metadata `videoReady: false` is the
					// normal desktop state for a few hundred milliseconds). What is genuinely diagnostic is a
					// viewer trying to use the player while metadata still has not arrived — this branch.
					console.warn("Tried playing video before canPlay is set; starting playback from the user gesture instead.", {
						recordingId: this.recordingId,
						readyState: video.readyState,
						controlsEnabled: this.controlsEnabled,
						showControlBar: this.showControlBar,
						breakpoint: this.display.name.value
					});
					this.isStarted = true;
					// Same bookkeeping as every other play path (the `canplay` handler and reloadAndPlay()):
					// `waiting` clears so the spinner does not outlive the gesture, and the promise is parked
					// on `playPromise` so a pause() immediately after the tap has something to await.
					this.waiting = false;
					try {
						this.playPromise = video.play();
						await this.playPromise;
					} catch (error) {
						// `isStarted` drives the started styling (object-fit: contain, black background), so
						// leaving it set after a rejection would show a "playing" frame with nothing in it.
						this.isStarted = false;
						this.playPromise = undefined;
						console.warn("LSP-19567: playback from the user gesture was rejected before canPlay", {
							recordingId: this.recordingId,
							readyState: video.readyState,
							error
						});
					}
					return;
				}

				if (this.playActionPromise) {
					try {
						await this.playActionPromise;
						return;
					} catch {
						// Bugbot (PR #4816): the in-flight play() this gesture coalesced onto was rejected
						// (a transient AbortError from a concurrent pause(), or an autoplay-policy
						// NotAllowedError) -- that is evidence about the OLD gesture, not this one. Re-enter
						// play() from the top instead of falling through to the issuance code below: while
						// this call was awaiting the coalesced promise, a concurrent pause() (scrub-start,
						// the clip-dialog `commenting` watcher, teardown) could have changed isScrubbing,
						// thumbnail, or unmounted state, and falling through would skip re-checking all of
						// that. A fresh this.play() call re-runs every guard against current state. By the
						// time this catch runs, the rejected call's own finally has already cleared
						// `this.playActionPromise`/`this.playPromise`, so the recursive call starts clean
						// rather than re-coalescing onto itself.
						return this.play();
					}
				}

				this.isStarted = true;

				const isPlaying = !video.paused && !video.ended && video.readyState > video.HAVE_CURRENT_DATA;

				if (!isPlaying) {
					// LSP-18073: was fire-and-forget (video.play() never awaited/propagated), so a real
					// rejection here (e.g. an iOS autoplay NotAllowedError -- exactly the case this file's
					// iOS fullscreen workaround targets) became an unhandled rejection instead of reaching
					// toggleFullscreen()'s try/catch around `await this.play()`. Awaiting it here lets that
					// catch actually observe it.
					this.transitionPlaybackState(PlaybackState.RECOVERING);
					const operationGeneration = ++this.playbackOperationGeneration;
					this.playOperationGeneration = operationGeneration;
					const playPromise = video.play();
					this.playActionPromise = playPromise;
					this.playPromise = playPromise;
					try {
						await playPromise;
					} catch (error) {
						if (operationGeneration === this.playbackOperationGeneration && this.playPromise === playPromise) {
							this.retireWaitingEpisode();
							this.resumeIntent = null;
							this.transitionPlaybackState(PlaybackState.PAUSED);
						}
						// LSP-20407: a pause() (or a teardown that detaches the element) landing on an
						// in-flight play() makes the browser reject that play() with an AbortError -- "The
						// play() request was interrupted by a call to pause(). https://goo.gl/LdLk22". It is
						// the documented outcome of the viewer's own pause/close, not a failure: the state
						// rollback above has already settled the player correctly.
						//
						// pause() has swallowed its own copy of this rejection since LSP-19523, but play()
						// kept rethrowing it, and the template binds `@click="play"` with no catch -- so Vue
						// routed it to the app root's errorCaptured and dropped a red banner showing that raw
						// Chrome text. 673 production sessions hit it in 30 days.
						//
						// Only AbortError is swallowed. A real playback failure (NotAllowedError from the
						// autoplay policy, NotSupportedError from a bad source) still propagates, which is
						// what toggleFullscreen()'s iOS pre-roll catch relies on to roll back.
						if (error instanceof Error && error.name === "AbortError") {
							console.warn("Interrupted play() promise rejected during play(); this is expected.", {
								recordingId: this.recordingId,
								error
							});
							return;
						}
						throw error;
					} finally {
						if (this.playActionPromise === playPromise) {
							this.playActionPromise = undefined;
						}
						if (operationGeneration === this.playbackOperationGeneration && this.playPromise === playPromise) {
							this.playPromise = undefined;
						}
					}
				} else {
					await this.pause();
				}
			},
			async pause({ preserveScrubState = false } = {}) {
				this.retireWaitingEpisode();
				const operationGeneration = ++this.playbackOperationGeneration;
				this.pauseOperationGeneration = operationGeneration;
				const pendingPlayPromise = this.playPromise;
				this.playActionPromise = undefined;
				const isActiveScrub = this.playbackState === PlaybackState.SCRUBBING && this.resumeIntent?.owner === "scrub";
				if (isActiveScrub && !preserveScrubState) {
					this.resumeIntent.shouldResume = false;
					this.resumeIntent.resumeSuppressed = true;
				} else if (!preserveScrubState) {
					this.resumeIntent = null;
					if (this.playbackState !== PlaybackState.ENDED) {
						this.transitionPlaybackState(PlaybackState.PAUSED);
					}
				}

				// Pause immediately. Awaiting an older play() before touching the element lets a later
				// scrub-resume transition overtake this request and then be paused by its stale continuation.
				this.$refs.video?.pause();

				if (pendingPlayPromise?.then) {
					try {
						await pendingPlayPromise;
					} catch (error) {
						// LSP-19523 architecture-gap round 21: the `video.pause()` call just above is exactly
						// what makes browsers reject an in-flight play() request (AbortError: "The play()
						// request was interrupted by a call to pause()"). That rejection is expected and
						// already handled -- pause() is the one who caused it -- so it must not propagate out
						// of pause() itself. Every production caller (the shouldPause watcher, onScrubStart(),
						// and play()'s own `await this.pause()` branch) invokes pause() without a catch, so an
						// unswallowed rejection here becomes a genuine unhandled promise rejection.
						console.warn("Interrupted play() promise rejected during pause(); this is expected.", {
							recordingId: this.recordingId,
							error
						});
					} finally {
						if (operationGeneration === this.playbackOperationGeneration && this.playPromise === pendingPlayPromise) {
							this.playPromise = undefined;
						}
					}
				}
			},
			reloadAndPlay() {
				const video = this.$refs.video;
				if (!video) return;

				try {
					this.stopPlaybackClock();
					this.retireWaitingEpisode();
					this.playbackOperationGeneration += 1;
					if (typeof video.load === "function") {
						video.load();
						this.syncPlaybackPosition(video);
					}

					if (!this.thumbnail && !this.shouldPause && !this.isScrubbing && typeof video.play === "function") {
						this.isStarted = true;
						this.resumeIntent = null;
						this.transitionPlaybackState(PlaybackState.RECOVERING);
						this.startPlaybackRequest("Playback recovery play() was rejected after reload");
					} else if (!this.isScrubbing) {
						this.resumeIntent = null;
						this.transitionPlaybackState(PlaybackState.PAUSED);
					}
				} catch (error) {
					console.warn("Playback recovery load/play setup threw synchronously", {
						recordingId: this.recordingId,
						error
					});
				}
			},
			onPlay() {
				this.handleNativePlay();
			},
			onPause() {
				this.handleNativePause();
			},
			setFullscreenData(state) {
				this.$refs.videoContainer.setAttribute("data-fullscreen", !!state);

				if (state) {
					this.$refs.video.classList.add("fullscreen");
				} else {
					this.$refs.video.classList.remove("fullscreen");
				}
			},
			sliderClicked(e) {
				e.preventDefault();
				e.stopPropagation();
			},
			async startPictureInPicture() {
				try {
					if (this.$refs.video !== document.pictureInPictureElement) {
						await this.$refs.video.requestPictureInPicture();
						this.isPictureInPicture = true;
					}
					window.setTimeout(() => {
						this.showSpeedControls = false;
						this.showMoreControls = false;
					}, 0);
				} catch (error) {
					console.error(error);
				}
			},
			// LSP-18073: the shared source-of-truth helper for deriving this player's fullscreen state --
			// used both unconditionally on every real transition (fullscreenChangeHandler,
			// webkitendfullscreen; no preceding error, just "the DOM changed, resync") and as the rollback
			// after any rejected/thrown vendor call in toggleFullscreen() below, re-deriving
			// isFullScreen/data-fullscreen from currentFullscreenElement() rather than blindly reverting to
			// the pre-call value -- a real fullscreenchange event can land between the optimistic set and
			// the rejection (e.g. another element grabbed fullscreen concurrently), and this same method
			// may have already corrected the state from that event by the time the catch runs.
			// $refs.videoContainer and $refs.video (read via setFullscreenData()) are both guarded by the
			// same check because Vue clears every template ref to null in lockstep once beforeUnmount()
			// completes (video uses v-show, not v-if, so it never unmounts independently of
			// videoContainer), and the toggleFullscreen() catch path can run after unmount if the vendor
			// promise settles late.
			syncFullscreenStateFromDom() {
				if (!this.$refs.videoContainer) {
					console.warn(
						"LSP-18073: cannot sync fullscreen state from DOM -- videoContainer is unavailable (likely called after unmount)",
						{
							recordingId: this.recordingId
						}
					);
					return;
				}
				const isThisPlayerFullscreen = this.currentFullscreenElement() === this.$refs.videoContainer;
				this.isFullScreen = isThisPlayerFullscreen;
				this.setFullscreenData(isThisPlayerFullscreen);
			},
			async toggleFullscreen() {
				// LSP-18073 PR review round 4: identity-check against this.$refs.videoContainer, not a
				// bare truthiness check -- if a SIBLING player's container currently owns fullscreen (the
				// ThumbnailTray grid mounts one VideoPlayer per artifact), a bare truthiness check would
				// take the exit branch and call document.exitFullscreen() on the sibling's fullscreen
				// instead of entering this player's own, matching the identity check already standardized
				// elsewhere in this file (syncFullscreenStateFromDom(), syncToastContainerForFullscreen()).
				if (this.currentFullscreenElement() === this.$refs.videoContainer) {
					// The document is in fullscreen mode. Set the optimistic exit state up front (so the
					// fullscreen-only controls disappear immediately), but roll it back if the vendor exit
					// call rejects or throws -- a real browser can deny/fail this (LSP-18073). We only listen
					// for fullscreenchange/webkitfullscreenchange (see fullscreenChangeHandler), not
					// fullscreenerror, so a legacy vendor call that resolves without ever entering/exiting
					// fullscreen (rather than rejecting) is not separately detected here.
					this.setFullscreenData(false);
					this.isFullScreen = false;
					let attemptedMethod = "none";
					try {
						if (document.exitFullscreen) {
							attemptedMethod = "exitFullscreen";
							await document.exitFullscreen();
						} else if (document.mozCancelFullScreen) {
							/* Firefox */
							attemptedMethod = "mozCancelFullScreen";
							await document.mozCancelFullScreen();
						} else if (document.webkitExitFullscreen) {
							/* Chrome, Safari and Opera */
							attemptedMethod = "webkitExitFullscreen";
							await document.webkitExitFullscreen();
						} else if (document.msExitFullscreen) {
							/* IE/Edge */
							attemptedMethod = "msExitFullscreen";
							await document.msExitFullscreen();
						} else {
							// LSP-18073: no exit method exists on `document` at all (e.g. a locked-down embed
							// stripped every vendor API) -- nothing was called, so nothing threw, and the
							// optimistic exit state above would otherwise go uncorrected with no diagnostic.
							console.warn(
								"LSP-18073: no vendor exitFullscreen method available; rolling back optimistic fullscreen-exit state",
								{
									recordingId: this.recordingId,
									attemptedMethod
								}
							);
							this.syncFullscreenStateFromDom();
						}
					} catch (error) {
						console.warn("LSP-18073: exitFullscreen call rejected or threw; rolling back optimistic fullscreen-exit state", {
							recordingId: this.recordingId,
							attemptedMethod,
							error
						});
						this.syncFullscreenStateFromDom();
					}
				} else {
					// The document is not in fullscreen mode

					let timeout = 1;
					this.isFullScreen = true;
					this.setFullscreenData(true);

					// Workaround for iOS issue. Start playing the video prior to requesting full screen.
					if (DeviceUtility.isIOS) {
						if (!this.isScrubbing) {
							try {
								if (this.$refs.video?.paused) {
									await this.play();
								} else if (this.playPromise) {
									await this.playPromise;
								}
							} catch (error) {
								// LSP-18073: play() -> pause() awaits this.playPromise with no catch of its own;
								// a real, documented browser behavior (e.g. AbortError from a concurrent
								// interrupting pause() call) can reject it, which would otherwise propagate
								// uncaught out of toggleFullscreen() itself, leaving the optimistic
								// isFullScreen/data-fullscreen set above uncorrected with no diagnostic.
								console.warn("LSP-18073: iOS play() workaround rejected; rolling back optimistic fullscreen-enter state", {
									recordingId: this.recordingId,
									error
								});
								this.syncFullscreenStateFromDom();
								return;
							}
						}
						timeout = 200;
					}

					// LSP-18073 (Cursor Bugbot): beforeUnmount()'s fullscreenEntryTimeoutId cleanup only
					// clears a timer already scheduled by the time it runs. If the component unmounts
					// while the awaited this.play() above is still pending (unbounded -- it depends on
					// real media buffering, not our control), beforeUnmount() finds no timer to clear yet;
					// scheduling one after that point would escape tracking entirely and, when it fires,
					// hit the deferred callback's own guard's warn -- the exact noise that cleanup exists
					// to prevent. Bail before scheduling if that race happened.
					if (!this.$refs.videoContainer) return;

					// LSP-18073: fullscreenEntryTimeoutId is a single scalar. A rapid double-click takes
					// this branch twice (it re-evaluates live DOM state, not an in-flight flag), and the
					// second assignment below would otherwise silently discard the first timer's ID,
					// orphaning it -- if unmount then happens before either fires, beforeUnmount() can only
					// clear the most recent one, and the orphaned first timer still fires post-unmount.
					if (this.fullscreenEntryTimeoutId) {
						clearTimeout(this.fullscreenEntryTimeoutId);
					}

					this.fullscreenEntryTimeoutId = setTimeout(async () => {
						this.fullscreenEntryTimeoutId = null;
						if (!this.$refs.videoContainer) {
							console.warn(
								"LSP-18073: cannot attempt fullscreen entry -- videoContainer is unavailable (likely unmounted before the deferred request ran)",
								{ recordingId: this.recordingId }
							);
							return;
						}
						let attemptedMethod = "none";
						try {
							if (this.$refs.videoContainer.requestFullscreen) {
								attemptedMethod = "requestFullscreen";
								await this.$refs.videoContainer.requestFullscreen();
							} else if (this.$refs.videoContainer.mozRequestFullScreen) {
								/* Firefox */
								attemptedMethod = "mozRequestFullScreen";
								await this.$refs.videoContainer.mozRequestFullScreen();
							} else if (this.$refs.videoContainer.webkitRequestFullscreen) {
								/* Chrome, Safari and Opera */
								attemptedMethod = "webkitRequestFullscreen";
								await this.$refs.videoContainer.webkitRequestFullscreen();
							} else if (this.$refs.video.webkitEnterFullScreen) {
								attemptedMethod = "webkitEnterFullScreen";
								await this.$refs.video.webkitEnterFullScreen();
							} else if (this.$refs.videoContainer.msRequestFullscreen) {
								/* IE/Edge */
								attemptedMethod = "msRequestFullscreen";
								await this.$refs.videoContainer.msRequestFullscreen();
							} else {
								// LSP-18073: no enter method exists at all -- nothing was called, so nothing
								// threw, and the optimistic enter state above would otherwise go uncorrected
								// with no diagnostic.
								console.warn(
									"LSP-18073: no vendor requestFullscreen method available; rolling back optimistic fullscreen-enter state",
									{ recordingId: this.recordingId, attemptedMethod }
								);
								this.syncFullscreenStateFromDom();
							}
						} catch (error) {
							console.warn(
								"LSP-18073: fullscreen enter call rejected or threw; rolling back optimistic fullscreen-enter state",
								{ recordingId: this.recordingId, attemptedMethod, error }
							);
							this.syncFullscreenStateFromDom();
						}
					}, timeout);
				}
			},
			onScrubStart() {
				const video = this.$refs.video;
				if (!video || this.isScrubbing) return;

				// LSP-19523 architecture-gap round 17: a `waiting` event that fired while paused (or
				// mid-scrub) schedules a 1000ms buffering timeout that only `pause()` retires. When the
				// video was already paused entering the scrub, `pause()` below is never called (shouldResume
				// is false), so retire any pending episode here unconditionally -- otherwise it survives the
				// whole scrub and flips playbackState to BUFFERING after onScrubEnd has already settled on
				// PAUSED.
				this.retireWaitingEpisode();
				const startedFromEnded = this.playbackState === PlaybackState.ENDED;
				const shouldResume =
					playingStates.has(this.playbackState) || this.playbackState === PlaybackState.RECOVERING || !video.paused;
				const scrubGeneration = this.playbackTransitionGeneration + 1;
				this.resumeIntent = {
					owner: "scrub",
					generation: scrubGeneration,
					shouldResume,
					endedDuringScrub: startedFromEnded,
					hasPositionUpdate: false,
					resumeSuppressed: false,
					startedFromEnded
				};
				this.transitionPlaybackState(PlaybackState.SCRUBBING);
				if (shouldResume) {
					this.pause({ preserveScrubState: true });
				}
			},
			onScrubEnd() {
				if (!this.isScrubbing) return;

				// LSP-19523 architecture-gap round 17: a `waiting` event can also fire mid-scrub (after
				// onScrubStart's own retire above), scheduling a fresh buffering timeout guarded only against
				// firing *while* SCRUBBING. Retire it here too so it cannot outlive the scrub and fire against
				// the PAUSED/RECOVERING state onScrubEnd is about to settle on.
				this.retireWaitingEpisode();
				this.restoreScrubOwnedPosition();
				const resumeIntent = this.resumeIntent?.owner === "scrub" ? this.resumeIntent : null;
				const isCurrentIntent = resumeIntent?.generation === this.playbackTransitionGeneration;
				const shouldResume = isCurrentIntent && resumeIntent.shouldResume && !resumeIntent.endedDuringScrub;
				// Bugbot (PR #4816): a release that isn't resuming always went to PAUSED, even when
				// `endedDuringScrub` recorded that playback genuinely reached its terminal position during
				// this scrub (and no later non-terminal drag cleared that, per onScrubStart/videoScrubbed).
				// Landing in PAUSED instead of ENDED let a subsequent `seeked` event recompute `percent`
				// from the (possibly fractional, just-under-duration) native playhead and pull the slider
				// off the end, and meant a later backward scrub-to-resume never fired since that path is
				// gated on actually being in ENDED.
				const endedDuringScrub = isCurrentIntent && resumeIntent.endedDuringScrub === true;
				this.resumeIntent = null;
				this.transitionPlaybackState(
					shouldResume ? PlaybackState.RECOVERING : endedDuringScrub ? PlaybackState.ENDED : PlaybackState.PAUSED
				);
				if (shouldResume) {
					this.isStarted = true;
					this.startPlaybackRequest("Playback resume after scrubbing was rejected");
				} else if (endedDuringScrub) {
					this.percent = this.maxSliderValue;
				}
				this.scrubReleaseOperationGeneration = this.playbackOperationGeneration;
			},
			videoScrubbed(value) {
				const video = this.$refs.video;
				if (!video) return;
				video.currentTime = (value / this.maxSliderValue) * this.duration;
				if (this.resumeIntent?.owner === "scrub") {
					const isTerminalPosition = value >= this.maxSliderValue;
					this.resumeIntent.endedDuringScrub = isTerminalPosition;
					this.resumeIntent.hasPositionUpdate = true;
					if (this.resumeIntent.startedFromEnded && !this.resumeIntent.resumeSuppressed && !isTerminalPosition) {
						this.resumeIntent.shouldResume = true;
					}
				}
				// Bugbot (PR #4816): syncPlaybackPosition() forces `percent` to 0 whenever currentTime is
				// under 0.2s -- correct for the RAF playback clock (see updateSlider()), where that guards
				// against a flicker on first load, but wrong here: the slider's own v-model already drove
				// `percent` to exactly `value` (this handler is `@update:modelValue`), so a deliberate drag
				// into the first fraction of a second was being snapped straight back to the origin. Assign
				// `percent` directly to the position the user actually dragged to instead.
				this.percent = value;
				this.currentTime = video.currentTime;
			}
		}
	};
</script>

<style scoped lang="scss">
	.artifact-item {
		padding: 0;
		border: 2px solid blue;
		margin-bottom: 8px;
	}

	.conversation-recordings > div:first-child {
		margin-bottom: 20px;
	}

	.conversation-recording {
		object-fit: contain;
		max-width: 100%;
	}

	.conversation-recording .fullscreen {
		height: 100%;
	}

	#video-container {
		height: 83px !important;
		border-radius: 16px !important;
	}

	.video-container {
		position: relative;
		color: #fff;
		width: 100%;
		background-color: #000;
		overflow: hidden;
		-webkit-transform: translateZ(0);
	}

	.pause-indicator {
		background-color: #00000066;
		color: #fff;
		border-radius: 50%;
		position: absolute;
		top: calc(50% - 20px);
		left: calc(50% - 20px);
		width: 40px;
		height: 40px;
		display: flex;
		justify-content: center;
		align-items: center;
		font-size: 2rem !important;
		z-index: 1;
	}

	.loading-indicator {
		position: absolute;
		top: calc(50% - 50px);
		left: calc(50% - 50px);
	}

	.video-processing {
		display: flex;
		height: 100%;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		text-align: center;
		border: 1px solid var(--ls-contact-theme-space-cadet-8, rgba(3, 1, 29, 0.08));
		background: var(
			--Video-Processing-Gradient,
			linear-gradient(254deg, #f7f5fe 0%, #f0f8ff 13.76%, #ebfffa 43.55%, #eaf9ff 76.6%, #fef5fd 99.13%)
		);
	}

	.video-processing .message {
		color: #000;
	}

	.video-processing > .refresh > .v-btn {
		background-color: #03011d80 !important;
		color: #fff !important;
	}

	.video-container.paused.scrubbing.wasPlaying::after {
		content: "";
		background: none;
	}

	.video-container.waiting .loading {
		position: absolute;
		top: calc(50% - 25px);
		left: calc(50% - 25px);
		color: gray;
		z-index: 1;
	}

	.video-container.fullscreen {
		height: 100%;
	}

	.video-container.fullscreen video {
		height: 100%;
	}

	video.thumbnail {
		height: 100%;
		width: 100%;
		background-color: #fff;
	}

	video.started {
		background-color: #000;
	}

	.video-container.fullscreen .video-controls {
		position: absolute;
		bottom: 0;
	}

	.speed-list .active {
		border-left: 4px solid var(--color-primary);
		background-color: rgba(0, 112, 255, 0.08);
	}
</style>
